import useAppModal from 'hooks/useAppModal';
import pageIntegrations from 'i18n/resources/pageIntegrations/en.json';
import {
  Integration,
  IntegrationEnum,
  InternalIntegrationSetting,
} from 'models/InternalIntegrationSetting';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { HOSTFULLY_ACCOUNTING_PATH } from 'pages/hostfullyAccounting/hostfullyAccounting.constants';
import Tooltip from 'components/tooltip/Tooltip';
import { Alert, Glyphicon } from 'react-bootstrap';
import { IntegrationDescriptionModalWrapper } from './IntegrationDescriptionModal.styles';
import {
  INTEGRATION_NAME_LINKS,
  SECONDARY_INTEGRATION_LINKS,
} from './IntegrationDescription.constants';
import HostfullyAccountingDescriptionBody from './hostfullyAccountingDescriptionBody/HostfullyAccountingDescriptionBody';

type MappedIntegrationsDescriptionsEnum =
  keyof typeof pageIntegrations.integrationsDescriptions;

const useIntegrationDescriptionModal = () => {
  const { openConfirmModal } = useAppModal();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getModalBody = ({
    integration: { enumId, displayName, name },
  }: {
    integration: Integration;
  }) => {
    if (enumId === IntegrationEnum.HOSTFULLYACCOUNTING) {
      return <HostfullyAccountingDescriptionBody />;
    }

    const integrationNameLink =
      INTEGRATION_NAME_LINKS[enumId as keyof typeof INTEGRATION_NAME_LINKS];
    const secondaryLink =
      SECONDARY_INTEGRATION_LINKS[
        enumId as keyof typeof SECONDARY_INTEGRATION_LINKS
      ];

    const shouldShowTooltip = [
      IntegrationEnum.STRIPE,
      IntegrationEnum.PAYPAL,
    ].includes(enumId);

    const integrationName = displayName ?? name;

    const isVrpIntegration = enumId === IntegrationEnum.VACATIONRENTPAYMENT;

    return (
      <IntegrationDescriptionModalWrapper>
        <p>
          <Trans
            i18nKey={`pageIntegrations.integrationsDescriptions.${
              enumId as MappedIntegrationsDescriptionsEnum
            }`}
            components={{
              bold: <b />,
              p: <p />,
              i: <i />,
              br: <br />,
              nameLink: (
                <Link
                  to={integrationNameLink}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Integration link"
                />
              ),
              secondaryLink: (
                <Link
                  to={secondaryLink}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Integration link"
                />
              ),
            }}
          >
            {t('common.notApplicable')}
          </Trans>

          {shouldShowTooltip && (
            <Tooltip
              text={t(
                'pageIntegrations.integrationsDescriptions.paymentFeeWarningTooltip',
                {
                  integration: integrationName,
                },
              )}
              id="integration-tooltip"
            >
              <Glyphicon glyph="info-sign" />
            </Tooltip>
          )}
        </p>

        {isVrpIntegration && (
          <Alert bsStyle="danger">
            {t(
              'pageIntegrations.integrationsDescriptions.vrpExclusiveActivation',
            )}
          </Alert>
        )}
      </IntegrationDescriptionModalWrapper>
    );
  };

  const openIntegrationDescriptionModal = ({
    integration,
  }: InternalIntegrationSetting) => {
    const isHostfullyAccounting =
      integration.enumId === IntegrationEnum.HOSTFULLYACCOUNTING;

    openConfirmModal({
      title: t('pageIntegrations.modals.viewIntegrationDescription.title', {
        integration: integration.displayName ?? integration.name,
      }),
      body: getModalBody({
        integration,
      }),
      onConfirm: isHostfullyAccounting
        ? () => navigate(HOSTFULLY_ACCOUNTING_PATH)
        : () => {},
      confirmLabel: t(
        `pageIntegrations.modals.viewIntegrationDescription.${
          isHostfullyAccounting ? 'learnMoreBtn' : 'confirmBtn'
        }`,
      ),
      hideCancel: !isHostfullyAccounting,
    });
  };

  return { openIntegrationDescriptionModal };
};

export default useIntegrationDescriptionModal;
